import { Link, useParams } from "react-router-dom";
import { SERVICES } from "../data/services";
import { SITE } from "../data/site";
import styles from "./ServicePage.module.css";

function pickSrcSet(srcSet: string | undefined, selector: "first" | "second"): string | undefined {
  if (!srcSet) return undefined;
  const parts = srcSet.split(",").map((item) => item.trim()).filter(Boolean);
  if (parts.length === 0) return undefined;
  if (selector === "first") return parts[0];
  return parts[1] ?? parts[0];
}

export function ServicePage() {
  const { slug } = useParams<{ slug: string }>();
  const service = SERVICES.find((item) => item.slug === slug);

  if (!service) {
    return (
      <main className={`container ${styles.notFound}`}>
        <p className={styles.eyebrow}>404 · Service not found</p>
        <h1 className={styles.heading}>We couldn't find that service.</h1>
        <p className={styles.lede}>
          The link may have moved or the service has been retired. The full list is on the home
          page, and live availability is on the Vagaro calendar.
        </p>
        <div className={styles.notFoundActions}>
          <Link to="/" className="btn btn--primary btn--lg">
            Back to home
          </Link>
          <Link to="/#book" className="btn btn--secondary btn--lg">
            Book an appointment
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main id="main">
      <section className={`container ${styles.hero}`}>
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <Link to="/">Home</Link> / <Link to="/#services">Services</Link> / {service.name}
        </nav>
        <p className={styles.eyebrow} data-reveal>{SITE.city} · Quiet luxury editorial</p>
        <h1 className={styles.heading} data-reveal>{service.name}</h1>
        <p className={styles.lede} data-reveal>{service.description}</p>
        <div className={styles.cta} data-reveal>
          <Link to={`/?service=${service.slug}#book`} className="btn btn--primary btn--lg">
            Book {service.name} <span aria-hidden="true">↗</span>
          </Link>
          <a className="btn btn--secondary btn--lg" href={`tel:${SITE.phoneHref}`}>
            Call {SITE.phone}
          </a>
        </div>
      </section>

      <div className={`container ${styles.section}`}>
        <figure className={styles.media} data-reveal>
          <picture>
            <source type="image/avif" srcSet={pickSrcSet(service.hero.srcSet, "first")} />
            <source type="image/webp" srcSet={pickSrcSet(service.hero.srcSet, "second")} />
            <img
              src={service.hero.src}
              alt={service.hero.alt}
              width={1200}
              height={800}
              loading="eager"
              fetchPriority="high"
            />
          </picture>
        </figure>

        <div data-reveal>
          <p className={styles.eyebrow}>The approach</p>
          <h2 className={styles.subheading}>How we get there</h2>
        </div>

        <ul className={styles.philosophy}>
          {service.philosophy.map((pillar) => (
            <li key={pillar.title} className={styles.card} data-reveal>
              <h3 className={styles.cardTitle}>{pillar.title}</h3>
              <p className={styles.cardBody}>{pillar.body}</p>
            </li>
          ))}
        </ul>

        <div className={styles.cta} data-reveal>
          <Link to={`/?service=${service.slug}#book`} className="btn btn--primary btn--lg">
            Book {service.name}
          </Link>
          <Link to="/#services" className="btn btn--secondary">
            See other services
          </Link>
        </div>
      </div>
    </main>
  );
}
